namespace Shared

open System
open Microsoft.FSharp.Control
open Microsoft.FSharp.Core
open Shared.Dto
open Shared.Dto.Dashboard
open Shared.Dto.Dto
open Shared.Dto.Form
open Shared.Dto.MapSensorData
open Shared.Dto.MapSensorSettings
open Shared.Dto.Gateways
open Shared.Dto.MyMapSensors
open Shared.Dto.MySensGateway
open Shared.Dto.Page.User.SensorDataPackets
open Shared.Dto.Page.UserList
open Shared.Dto.RimPro
open Shared.Dto.Sensor
open Shared.Dto.SensorGraphData
open Shared.Dto.User
open Shared
open Shared.Dto.Page
open Shared.Dto.Page.User
open Shared.DtoTypes
open Shared.DtoTypes.DeviceLocation
open Shared.DtoTypes.Page
open Shared.DtoTypes.PaginatedRequest
open Shared.DtoTypes.PhysicalSensor
open Shared.WithLastDate

module Route =
    let builder typeName methodName =
        sprintf "/api/%s/%s" typeName methodName

type MySenseApi = {
    getSession: SessionKey option -> Async<Session>

    getPublicSensors: Unit -> Async<MapSensor list>

    getSensors: SessionKey -> Async<AuthenticatedResponse<MapSensor list>>

    login: LoginRequestData -> Async<LoginResponse>
    logout: SessionKey -> Async<Unit>

    refreshTtnEndDevices: AuthenticatedRequest<unit> -> Async<PhysicalSensorRefresh.Result>

    refreshTtiEndDevices: AuthenticatedRequest<unit> -> Async<PhysicalSensorRefresh.Result>

    getAllPhysicalSensors: Unit -> Async<PhysicalSensorWithLastData list>

    getConfiguredTtnSensors: Unit -> Async<ConfiguredPhysicalSensor list>

    createPhysicalSensor:
        AuthenticatedRequest<ConfiguredPhysicalSensor> -> Async<AuthenticatedResponse<ConfiguredPhysicalSensor option>>

    removePhysicalSensor: AuthenticatedRequest<string> -> Async<AuthenticatedResponse<unit>>

    updatePhysicalSensor:
        AuthenticatedRequest<ConfiguredPhysicalSensor> -> Async<AuthenticatedResponse<ConfiguredPhysicalSensor option>>

    getUnusedPhysicalSensors: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<ConfiguredPhysicalSensor list>>

    getAllMySensSensors: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<IdValue<MapSensorDto> list>>

    getMySensSensor: int -> Async<MapSensorDto option>

    getSensorType: int -> Async<SensorModel>

    getHistoricMapSensorData: SensorDataRequest -> Async<GraphDataDto>

    getSensorDetailPageData: AuthenticatedRequest<int> -> Async<SensorDetailPage.Response>

    downloadMapSensorData: AuthenticatedRequest<SensorDataRequest> -> Async<AuthenticatedResponse<byte[]>>

    getAllMySensSensorsWithOptionalLastDate: Unit -> Async<WithOptionalLastDate<IdValue<MapSensorDto>> list>

    createMapSensor: AuthenticatedRequest<MapSensorDto> -> Async<AuthenticatedResponse<IdValue<MapSensorDto> option>>

    updateMapSensor:
        AuthenticatedRequest<IdValue<MapSensorDto>> -> Async<AuthenticatedResponse<IdValue<MapSensorDto> option>>

    deleteMySensSensor: int -> Async<bool>

    updatePassword: AuthenticatedRequest<string * string> -> Async<bool>

    getAllFullUsers: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<IdValue<FullUserDto> list>>

    getAllUsers: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<UserDto list>>

    createUser: FullUserDto -> Async<bool>

    updateUser: IdValue<FullUserDto> -> Async<bool>

    deleteUser: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<bool>>

    addPackage: int * int -> Async<bool>

    sendPasswordResetRequest: string -> Async<bool>

    getPasswordResetRequest: string -> Async<PasswordResetRequest option>

    resetPassword: string * string -> Async<bool>

    getAllGateways: AuthenticatedRequest<Unit> -> Async<AuthenticatedResponse<IdValue<MySensGateway> list>>

    loadGatewayStatus: AuthenticatedRequest<string list> -> Async<AuthenticatedResponse<GatewayWithStatus list>>

    refreshGatewaysFromTtn: Unit -> Async<bool>

    refreshGatewaysFromTti: Unit -> Async<bool>

    updateGateway: IdValue<MySensGateway> -> Async<bool>

    getLeafletWetnessConfiguration: Unit -> Async<LeafletWetnessConfiguration>

    updateLeafletWetnessConfiguration: LeafletWetnessConfiguration -> Async<bool>

    getContactInfos: AuthenticatedRequest<Unit> -> Async<AuthenticatedResponse<AlertDto.ContactInfo list>>

    getMyContactInfos: AuthenticatedRequest<Unit> -> Async<AuthenticatedResponse<AlertDto.ContactInfo list>>

    createContactInfo: AuthenticatedRequest<AlertDto.NewContactInfo> -> Async<AuthenticatedResponse<bool>>

    updateContactInfo: AuthenticatedRequest<AlertDto.ContactInfo> -> Async<AuthenticatedResponse<bool>>

    deleteContactInfo: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<bool>>

    getRimProAvailableSensors: AuthenticatedRequest<Unit> -> Async<AuthenticatedResponse<SensorsResponseDto>>

    getMyAirTemperatureMapSensors:
        AuthenticatedRequest<Unit> -> Async<AuthenticatedResponse<IdValue<MapSensorDto> list>>

    getAlerts: AuthenticatedRequest<Unit> -> Async<AuthenticatedResponse<AlertDto.RunningAlert list>>
    getMyAlerts: AuthenticatedRequest<Unit> -> Async<AuthenticatedResponse<AlertDto.RunningAlert list>>
    createAlert: AuthenticatedRequest<AlertDto.NewAlert> -> Async<AuthenticatedResponse<bool>>
    deleteAlert: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<bool>>

    buyAlertPackages: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<bool>>

    getMyMapSensorsData: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<MapSensorDataResponse>>

    getPurchasedAlertCounts: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<AlertDto.PurchasedAlertCount>>

    getMapSensorSettings: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<MapSensorSettingsDto>>

    updateMapSensorSettings: AuthenticatedRequest<UpdatedMapSensorSettingsDto> -> Async<AuthenticatedResponse<bool>>

    getMyMapSensors: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<MyMapSensorDto list>>

    activateMapSensor: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<bool>>

    disableMapSensor: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<bool>>

    getAlertMessages:
        AuthenticatedRequest<Pagination> -> Async<AuthenticatedResponse<PersistedAlertMessage.Message list>>

    getMyRimProStations: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<StationDto list>>

    deleteRimProStation: AuthenticatedRequest<Guid> -> Async<AuthenticatedResponse<bool>>

    createRimProStation: AuthenticatedRequest<NewRimProStationDto> -> Async<AuthenticatedResponse<bool>>

    getMyGateways: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<MyGatewaysResponse>>

    getDashboard: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<DashboardResponse>>

    getScabStation: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<ScabData.StationResponse>>
    getScabData: AuthenticatedRequest<ScabData.DataRequest> -> Async<AuthenticatedResponse<ScabData.DataResponse>>

    getMyScabStations: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<MyScabStations.ListingDto list>>

    renameScabStation: AuthenticatedRequest<MyScabStations.RenameRequest> -> Async<AuthenticatedResponse<unit>>

    deleteScabStation: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<MyScabStations.DeletionResponse>>

    getMyUserGroups: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<MyUserGroups.GroupListingData list>>

    getPeronosporaStation: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<PeronosporaData.StationResponse>>
    getPeronosporaData:
        AuthenticatedRequest<PeronosporaData.DataRequest> -> Async<AuthenticatedResponse<PeronosporaData.DataResponse>>

    getMyPeronosporaStations:
        AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<MyPeronosporaStations.ListingDto list>>

    renamePeronosporaStation:
        AuthenticatedRequest<MyPeronosporaStations.RenameRequest> -> Async<AuthenticatedResponse<unit>>

    deletePeronosporaStation:
        AuthenticatedRequest<int> -> Async<AuthenticatedResponse<MyPeronosporaStations.DeletionResponse>>

    getMyProfile: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<FullUserDto>>

    getAllGroups: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<GroupList.GroupListingData list>>

    createUserGroup:
        AuthenticatedRequest<GroupList.CreateUserGroupRequest>
            -> Async<AuthenticatedResponse<GroupList.CreateUserGroupResponse>>

    deleteUserGroup: AuthenticatedRequest<GroupList.DeleteUserGroupRequest> -> Async<AuthenticatedResponse<unit>>

    getAllPackages: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<IdValue<Package> list>>

    setUserIsBlocked: AuthenticatedRequest<UpdateUserIsBlockedRequest> -> Async<AuthenticatedResponse<bool>>

    getAllDeviceLocations: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<DeviceLocation list>>

    getMyTalgilTriggers: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<MyTalgilTriggers.ListingDto list>>

    getMySoilSensors: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<IdValue<SoilMapSensorDto> list>>

    startTalgilProgram: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<MyTalgilTriggers.StartProgramResponse>>

    deleteTalgilTrigger: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<bool>>

    createTalgilTrigger:
        AuthenticatedRequest<TalgilTriggerForm.NewTriggerDto>
            -> Async<AuthenticatedResponse<MyTalgilTriggers.ListingDto>>

    getTalgilControllerInfo:
        AuthenticatedRequest<int> -> Async<AuthenticatedResponse<TalgilTriggerForm.ControllerInfoDto>>

    setIsActiveTalgilTrigger:
        AuthenticatedRequest<MyTalgilTriggers.SetIsActiveRequestDto> -> Async<AuthenticatedResponse<bool>>

    getRimProIframes: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<RimProIframeDto list>>

    getMyTipBurnStations: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<TipBurn.StationListingDto list>>

    getTipBurnStation: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<TipBurnData.StationResponse>>
    getTipBurnData:
        AuthenticatedRequest<TipBurnData.DataRequest> -> Async<AuthenticatedResponse<TipBurnData.DataResponse>>

    renameTipBurnStation: AuthenticatedRequest<TipBurn.RenameRequest> -> Async<AuthenticatedResponse<unit>>

    deleteTipBurnStation: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<TipBurn.DeletionResponse>>

    impersonateUser: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<bool>>

    stopImpersonation: AuthenticatedRequest<unit> -> Async<AuthenticatedResponse<unit>>

    getReceivedSensorPackets:
        AuthenticatedRequest<LoadSensorPacketsRequest> -> Async<AuthenticatedResponse<SensorPacketDto list>>

    getSensorName: AuthenticatedRequest<int> -> Async<AuthenticatedResponse<SensorNameDto>>

    getTalgilTriggerExecutionLog:
        AuthenticatedRequest<TalgilTriggerExecutionLog.DataRequest>
            -> Async<AuthenticatedResponse<Talgil.TriggerExecutionDto list>>

    getTalgilTriggerNames:
        AuthenticatedRequest<int> -> Async<AuthenticatedResponse<TalgilTriggerExecutionLog.TriggerNamesDto>>
}